import { api } from "./client";
import { noteLunchServerTime } from "../lib/lunchClock";

// Translation type (used by multiple entities)
export interface Translation {
  id: number;
  languageCode: string;
  name: string;
}

// ItemLang type (specific for items)
export interface ItemLang {
  id: number;
  languageCode: string;
  itemId: number;
  name: string;
  description: string | null;
  unitAndSize: string | null;
}

// Item type
export interface Item {
  id: number;
  slug: string;
  pluCode: number | null;
  kcal: number | null;
  images: string | null;
  sortIndex: number | null;
  prepareTime: number | null;
  dds: number | null;
  categoryId: number | null;
  enabled: boolean;
  // Станциите на продаваемия — ВИНАГИ непразен списък (продукт без станция не
  // може да се маркира; org админът изисква станция).
  stationIds: number[];
  productTypeId: number | null;
  mymenu: boolean;
  delivery: boolean;
  kiosk: boolean;
  display: boolean;
  createdAt: string;
  updatedAt: string;
  price: number;
  lockedPrice: boolean;
  menuId: number;
  menuPriority: number | null;
  itemLangs: ItemLang[];
  allergenIds: number[];
  tagIds: number[];
  addonIds: number[];
  modifierIds: number[];
  // Само за копията от обедно меню (виж lib/lunchCatalog.ts): `id` е
  // синтетичен (отрицателен), истинският артикул/рецепта е `sourceItemId`,
  // а редът в сметката носи `lunchMenuId`.
  lunchMenuId?: number;
  sourceItemId?: number;
  // Порциите, които остават днес от обедното меню (дневен лимит − продадени);
  // null/липсва = без ограничение. Без кошницата на този терминал.
  lunchRemaining?: number | null;
}

// Category type
export interface Category {
  id: number;
  slug: string;
  parentId: number | null;
  sortIndex: number | null;
  images: string | null;
  isActive: boolean;
  // Hex цвят на групата (#abcabc, org колона categories.color, 0.0.234);
  // null → „Маркиране" взема цвета на ПРЕКИЯ родител (само едно ниво);
  // и той без цвят → неутрална (без измислен цвят). Изключение: групата
  // „Обедно меню" (строи се във FE, lib/lunchCatalog.ts) носи CSS променлива
  // `var(--color-pos-lunch)` — цветът се ползва само като CSS стойност.
  color: string | null;
  translations: Translation[];
}

// Allergen type
export interface Allergen {
  id: number;
  slug: string;
  image: string | null;
  translations: Translation[];
}

// Tag type
export interface Tag {
  id: number;
  slug: string;
  image: string | null;
  type: string | null;
  translations: Translation[];
}

// Station type
export interface Station {
  id: number;
  slug: string;
  color: string | null;
  sortIndex: number | null;
  translations: Translation[];
}

// Vat type
export interface Vat {
  id: number;
  vatName: string;
  vatRate: number;
}

// AddonItem type
export interface AddonItem {
  itemId: number;
  addonItemPrice: number | null;
  price: number;
}

// Addon type
export interface Addon {
  id: number;
  slug: string;
  note: string | null;
  isActive: boolean;
  maxQty: number | null;
  // 1 = самостоятелно количество · 2 = задължителна · 3 = обикновена.
  // BE праща ЧИСЛО — сравнявай с числа, без String() обвивки.
  type: number | null;
  itemId: number | null;
  categoryId: number | null;
  translations: Translation[];
  items: AddonItem[];
}

// Modifier type
export interface Modifier {
  id: number;
  slug: string;
  note: string | null;
  isActive: boolean;
  // при true продукт с този модификатор отваря ModifyItemModal при маркиране
  isRequired: boolean;
  translations: Translation[];
}

// Продукт в обедно меню — реферира артикула/рецептата от `items` по id
export interface LunchMenuCatalogItem {
  itemId: number;
  price: number;
  // {"bg": "300 г"}; липсващ език → unitAndSize на продукта
  unitAndSizeLangs: Record<string, string> | null;
  // Порции на ден (0 = без ограничение) и продадените днес към `serverTime`
  // (касата офлайн добавя своите). Липсват при огледало отпреди лимита.
  dailyLimit?: number;
  soldToday?: number;
}

// Обедно меню (днешно или предстоящо) с графика си — „Маркиране" го показва
// като отделна група, докато графикът покрива часовника (lib/lunchCatalog.ts)
export interface LunchMenuCatalog {
  id: number;
  name: string;
  dateFrom: string | null;
  dateTo: string | null;
  // 0 = неделя … 6 = събота; null → всеки ден
  weekdays: number[] | null;
  // "ЧЧ:ММ"; „до" е включително до края на минутата
  timeFrom: string | null;
  timeTo: string | null;
  items: LunchMenuCatalogItem[];
}

// Menu Response type
export interface MenuResponse {
  items: Item[];
  categories: Category[];
  allergens: Allergen[];
  tags: Tag[];
  stations: Station[];
  vats: Vat[];
  addons: Addon[];
  modifiers: Modifier[];
  // Обедните менюта, които още не са свършили — активното се избира на FE
  lunchMenus?: LunchMenuCatalog[];
  // Часовникът на сървъра при отговора (lib/lunchClock.ts)
  serverTime?: string | null;
}

/**
 * Fetch menu for a specific restaurant
 * @param restaurantName - Name of the restaurant
 * @returns Menu data with all related entities
 */
export const getMenu = async (
  restaurantName: string,
): Promise<MenuResponse> => {
  const response = await api.get<MenuResponse>("/menu/", {
    params: { restaurant_name: restaurantName },
  });
  // Часовникът на обедните менюта се сверява само по облака — копието в
  // касата (офлайн) носи времето, когато е записано
  if (!response.config.edgeLocal && !response.config.edgeRetried)
    noteLunchServerTime(response.data?.serverTime);
  return response.data;
};
